import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, PenLine, Star } from "lucide-react";
import { GOOGLE_REVIEW_URL } from "../site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GoogleG = ({ className = "h-8 w-8" }) => (
  <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
  </svg>
);

const Stars = ({ value = 0, size = "h-4 w-4" }) => (
  <span className="flex gap-0.5" aria-label={`Note ${value} sur 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`${size} ${
          i <= Math.round(value) ? "fill-amber-400 text-amber-400" : "fill-slate-200 text-slate-200"
        }`}
      />
    ))}
  </span>
);

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

export default function GoogleReviews() {
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    let alive = true;
    fetch(`${API}/reviews`)
      .then((r) => r.json())
      .then((d) => alive && setState({ loading: false, data: d }))
      .catch(() => alive && setState({ loading: false, data: null }));
    return () => {
      alive = false;
    };
  }, []);

  const { loading, data } = state;
  const ready = data && data.configured && !data.error && data.rating != null;
  const reviews = ready ? data.reviews || [] : [];

  return (
    <section id="avis" data-testid="google-reviews-section" className="bg-[#F0F4F8] py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-12 flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between"
        >
          <div className="max-w-2xl">
            <p className="mb-4 text-xs font-semibold uppercase tracking-[0.25em] text-teal-700 sm:text-sm">
              Avis Google
            </p>
            <h2 className="font-serif text-3xl font-medium tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Ils nous confient leurs textiles
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
              Les avis publiés sur notre fiche Google, affichés ici
              automatiquement et mis à jour en continu.
            </p>
          </div>

          {ready && (
            <div
              data-testid="google-rating-summary"
              className="flex items-center gap-5 rounded-3xl border border-slate-200 bg-white px-8 py-6 shadow-sm"
            >
              <GoogleG className="h-10 w-10" />
              <div>
                <div className="flex items-center gap-3">
                  <span className="font-serif text-4xl font-semibold text-slate-900">
                    {data.rating.toFixed(1).replace(".", ",")}
                  </span>
                  <Stars value={data.rating} size="h-5 w-5" />
                </div>
                <p className="mt-1 text-sm text-slate-500">
                  Basé sur {data.userRatingCount ?? 0} avis Google
                </p>
              </div>
            </div>
          )}
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-48 animate-pulse rounded-3xl bg-slate-200/60" />
            ))}
          </div>
        ) : ready && reviews.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {reviews.map((r, i) => (
              <motion.article
                key={r.googleMapsUri || `${r.author}-${i}`}
                data-testid="google-review-card"
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.12 }}
                className="flex flex-col rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-teal-600 font-serif text-lg font-semibold text-white">
                    {(r.author || "G").charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-900">{r.author || "Client Google"}</p>
                    <div className="flex items-center gap-2">
                      <Stars value={r.rating ?? 0} />
                      <span className="text-xs text-slate-500">
                        {r.relativeTime || (r.publishTime ? formatDate(r.publishTime) : "")}
                      </span>
                    </div>
                  </div>
                </div>
                {r.text && (
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-600">{r.text}</p>
                )}
                <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
                  <GoogleG className="h-4 w-4" />
                  {r.googleMapsUri && (
                    <a
                      href={r.googleMapsUri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-semibold text-teal-700 hover:text-teal-900"
                    >
                      Voir sur Google
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </motion.article>
            ))}
          </div>
        ) : (
          <motion.div
            data-testid="google-reviews-fallback"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex flex-col items-center rounded-3xl border border-dashed border-slate-300 bg-white/70 p-12 text-center"
          >
            <GoogleG className="h-12 w-12" />
            <h3 className="mt-5 font-serif text-2xl font-semibold text-slate-900">
              Votre avis compte
            </h3>
            <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base">
              Vous avez fait appel à PureTextiles ? Partagez votre expérience
              sur Google — les avis publiés s'afficheront ici automatiquement.
            </p>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-leave-review-btn"
              className="mt-7 inline-flex items-center gap-2 rounded-full bg-slate-900 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-teal-700 hover:shadow-xl"
            >
              <PenLine className="h-4 w-4" />
              Laisser un avis Google
            </a>
          </motion.div>
        )}

        {ready && (
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-leave-review-btn"
              className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-teal-700 hover:shadow-xl"
            >
              <PenLine className="h-4 w-4" />
              Laisser un avis Google
            </a>
            {data.reviewsUri && (
              <a
                href={data.reviewsUri}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="google-all-reviews-link"
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-slate-800 transition-all duration-200 hover:-translate-y-0.5 hover:border-teal-500 hover:text-teal-700 hover:shadow-lg"
              >
                Voir tous les avis sur Google
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
